import IndexedDb from '../Dependency/IndexedDb';
import { nativeQueue } from '../Dependency/NativeAppMemory';

const database = 'test';
const tables = ['test', 'trees', 'validated_queue'];
export const TABLE_TREES = 'trees';
export const TABLE_VALIDATED_QUEUE = 'validated_queue';
const db = new IndexedDb(database, tables);

// In the app the queue lives in the shell's SQLite store, so the page never opens IndexedDB for it. Both answer
// to the same operations, which is why nothing above this file changes
const store = () => (nativeQueue.isAvailable() ? nativeQueue : db);

async function openDb(onError?: Function) {
  if (nativeQueue.isAvailable()) {
    return nativeQueue.openDb().catch(err => {
      console.error(err);
      onError && onError(err);
    });
  }

  return db.createObjectStore(tables).catch(err => {
    console.error(err);
    onError && onError(err);
  });
}

async function countRecords(tablename: string, onError?: Function) {
  const count = await store().count(tablename).catch(err => {
    console.error(err);
    onError && onError(err);
  });
  return count;
}

function deleteDb(force = false, onError?: Function) {
  // A queued tree and its photo are the only copy there is: emptying the store takes an explicit `force`
  const wipe = async () => {
    if (!force) {
      const pending = await countRecords(TABLE_TREES).catch(() => undefined);

      if (pending) {
        console.error('refused to empty the queue: it still holds trees waiting to be uploaded');
        return;
      }
    }

    // The IndexedDB wrapper wipes the database, the native one empties its tables
    const operation = nativeQueue.isAvailable() ? nativeQueue.deleteDb(force) : db.delete();

    return operation.catch((err: any) => {
      console.error(err);
      onError && onError(err);
    });
  };

  return wipe();
}

function getValue(tableName: string, id: number, onError?: Function) {
  return store().getValue(tableName, id).catch(err => {
    console.error(err);
    onError && onError(err);
  });
}

function getAllValue(tableName: string, onError?: Function) {
  return store().getAllValue(tableName).catch(err => {
    console.error(err);
    onError && onError(err);
  });
}

function getAllKeys(tableName: string, onError?: Function) {
  return store().getAllKeys(tableName).catch(err => {
    console.error(err);
    onError && onError(err);
  });
}

// The trees a row holds, without its photo: a walk over the queue reads every row, and the photo is what a
// row mostly is
function getTreeNumber(tableName: string, id: any) {
  return store().getNumber(tableName, id).catch(() => 0);
}

// The trees of the whole queue, when the store counts them itself (`null` when it cannot)
function countTrees(tableName: string): Promise<number | null> {
  return store().totalTrees(tableName).catch(() => null);
}

function putValue(tableName: string, value: object, key?: string, onError?: Function) {
  return store().putValue(tableName, value, key).catch(err => {
    console.error(err);
    onError && onError(err);
  });
}

function putBulkValue(tableName: string, value: object[], onError?: Function) {
  return store().putBulkValue(tableName, value).catch(err => {
    console.error(err);
    onError && onError(err);
  });
}

async function deleteValue(tableName: string, id: any, onError?: Function) {
  return await store().deleteValue(tableName, id).catch(err => {
    console.error(err);
    onError && onError(err);
  });
}

async function getCursor(tableName: string, callback: Function) {
  let cursor = await store().getCursor(tableName);
  while (cursor) {
    await callback(cursor);
    cursor = cursor && await cursor.continue();
  }
}

function getIDB() {
  return db;
}

export default {
  openDb,
  deleteDb,
  getValue,
  getAllKeys,
  getAllValue,
  getTreeNumber,
  countTrees,
  putValue,
  putBulkValue,
  deleteValue,
  getCursor,
  countRecords,
  getIDB
};
